import { format, isValid, parse } from 'date-fns'

import { UserLanguage } from '../../../../features/account/types/User'
import { NewsEntry, NewsEntryType, UnifiedNewsEntry } from '../../../../features/news/types/UnifiedNewsEntry'
import { ContentLanguageTranslation } from '../../../types/InternalAnalystComment'
import { UploadedFile } from '../../../types/UploadedFile'
import {
  EditorialCard,
  EditorialCardAction,
  EditorialCardActionType,
  EditorialCardEntryData,
  EditorialCardMutation,
  EditorialCardStatus,
  StickyEmail,
} from '../types/EditorialCard'

export type EditorialCardLanguage = Extract<UserLanguage, 'en' | 'ja' | 'zh'>

export const editorialCardLanguages: EditorialCardLanguage[] = ['en', 'ja', 'zh']

export type EditorialCardTranslations = Record<EditorialCardLanguage, string>

export type EditorialCardFormValues = {
  titles: EditorialCardTranslations
  subtitles: EditorialCardTranslations
  contents: EditorialCardTranslations
  image: UploadedFile | null
  actionType: EditorialCardActionType
  actionTarget: string
  actionLabels: EditorialCardTranslations
  published: boolean
  sticky: boolean
  stickyEmail: StickyEmail
  // Milliseconds, so an untouched stored value is sent back exactly.
  publishedAt: number
}

export type EditorialCardDisplayStatus = 'published' | 'scheduled' | 'draft'

export type EditorialCardField = 'titles' | 'subtitles' | 'content' | 'image' | 'action' | 'sticky' | 'stickyEmail' | 'publishedAt' | 'status'

export const stickyEmailOptions: { value: StickyEmail; labelKey: string }[] = [
  { value: StickyEmail.No, labelKey: 'internal-marketing:editorial_cards_sticky_email_no' },
  { value: StickyEmail.First, labelKey: 'internal-marketing:editorial_cards_sticky_email_first' },
  { value: StickyEmail.Second, labelKey: 'internal-marketing:editorial_cards_sticky_email_second' },
  { value: StickyEmail.Last, labelKey: 'internal-marketing:editorial_cards_sticky_email_last' },
]

export const editorialCardStatusLabelKeys: Record<EditorialCardDisplayStatus, string> = {
  published: 'internal-marketing:editorial_cards_status_published',
  scheduled: 'internal-marketing:editorial_cards_status_scheduled',
  draft: 'internal-marketing:editorial_cards_status_draft',
}

export const editorialCardFieldLabelKeys: Record<EditorialCardField, string> = {
  titles: 'internal-marketing:editorial_cards_title',
  subtitles: 'internal-marketing:editorial_cards_subtitle',
  content: 'internal-marketing:editorial_cards_content',
  image: 'internal-marketing:editorial_cards_header_image',
  action: 'internal-marketing:editorial_cards_call_to_action',
  sticky: 'internal-marketing:editorial_cards_sticky',
  stickyEmail: 'internal-marketing:editorial_cards_sticky_email',
  publishedAt: 'internal-marketing:editorial_cards_published_on',
  status: 'internal-marketing:editorial_cards_published',
}

export const toTranslations = (map?: ContentLanguageTranslation | null): EditorialCardTranslations => ({
  en: map?.en ?? '',
  ja: map?.ja ?? '',
  zh: map?.zh ?? '',
})

const translationsEqual = (a?: ContentLanguageTranslation | null, b?: ContentLanguageTranslation | null) => {
  const left = toTranslations(a)
  const right = toTranslations(b)
  return editorialCardLanguages.every((language) => left[language] === right[language])
}

const toStoredImage = (image: EditorialCardEntryData['image']): UploadedFile | null =>
  image?.url ? { id: image.id ?? '', url: image.url, mimeType: image.mimeType ?? '' } : null

const toStickyEmail = (value?: number): StickyEmail => stickyEmailOptions.find((option) => option.value === value)?.value ?? StickyEmail.No

export const toEditorialCardFormValues = (card: EditorialCard | undefined, now = Date.now()): EditorialCardFormValues => {
  const action = card?.entryData.action
  return {
    titles: toTranslations(card?.titles),
    subtitles: toTranslations(card?.subtitles),
    contents: toTranslations(card?.entryData.content),
    image: toStoredImage(card?.entryData.image),
    actionType: action?.type === EditorialCardActionType.OpenSaasPage ? EditorialCardActionType.OpenSaasPage : EditorialCardActionType.None,
    actionTarget: action?.target ?? '',
    actionLabels: toTranslations(action?.label),
    published: card?.status === EditorialCardStatus.Published,
    sticky: !!card?.entryData.sticky,
    stickyEmail: toStickyEmail(card?.entryData.stickyEmail),
    publishedAt: card?.publishedAt ?? now,
  }
}

const toAction = (values: EditorialCardFormValues): EditorialCardAction =>
  values.actionType === EditorialCardActionType.OpenSaasPage
    ? { type: EditorialCardActionType.OpenSaasPage, target: values.actionTarget.trim(), label: values.actionLabels }
    : { type: EditorialCardActionType.None }

// Legacy's body: `entryData` is rebuilt from the form, and the backend replaces the stored one with it.
export const toEditorialCardMutation = (
  card: EditorialCard | undefined,
  values: EditorialCardFormValues,
  userEmail: string,
  now = Date.now()
): EditorialCardMutation => {
  const entryData: EditorialCardEntryData = {
    content: values.contents,
    ...(values.image ? { image: values.image } : {}),
    action: toAction(values),
    sticky: values.sticky,
    stickyEmail: values.stickyEmail,
    ...(card?.entryData.notifiedSlack !== undefined ? { notifiedSlack: card.entryData.notifiedSlack } : {}),
  }
  const common = {
    type: 'editorial' as const,
    titles: values.titles,
    subtitles: values.subtitles,
    entryData,
    market: 'us',
    publishedAt: values.publishedAt,
    status: values.published ? EditorialCardStatus.Published : EditorialCardStatus.Draft,
  }
  if (!card) return { ...common, createdAt: now, createdBy: userEmail }
  return { id: card.id, ...common, createdBy: card.createdBy, createdAt: card.createdAt, modifiedBy: userEmail }
}

// Publishing, or putting an already published card into the customer e-mail, reaches customers.
export const needsPublishConfirmation = (card: EditorialCard | undefined, values: EditorialCardFormValues) => {
  if (!values.published) return false
  if (card?.status !== EditorialCardStatus.Published) return true
  return values.stickyEmail !== StickyEmail.No && values.stickyEmail !== toStickyEmail(card.entryData.stickyEmail)
}

export const getEditorialCardStatus = (card: Pick<EditorialCard, 'status' | 'publishedAt'>, now = Date.now()): EditorialCardDisplayStatus => {
  if (card.status !== EditorialCardStatus.Published) return 'draft'
  return card.publishedAt <= now ? 'published' : 'scheduled'
}

const actionsEqual = (sent?: EditorialCardAction, stored?: EditorialCardAction) => {
  const sentType = sent?.type ?? EditorialCardActionType.None
  if (sentType !== (stored?.type ?? EditorialCardActionType.None)) return false
  if (sentType !== EditorialCardActionType.OpenSaasPage) return true
  return (sent?.target ?? '') === (stored?.target ?? '') && translationsEqual(sent?.label, stored?.label)
}

export const getUnstoredEditorialCardFields = (sent: EditorialCardMutation, stored: EditorialCard): EditorialCardField[] => {
  const checks: [EditorialCardField, boolean][] = [
    ['titles', translationsEqual(sent.titles, stored.titles)],
    ['subtitles', translationsEqual(sent.subtitles, stored.subtitles)],
    ['content', translationsEqual(sent.entryData.content, stored.entryData?.content)],
    ['image', (sent.entryData.image?.url ?? '') === (stored.entryData?.image?.url ?? '')],
    ['action', actionsEqual(sent.entryData.action, stored.entryData?.action)],
    ['sticky', !!sent.entryData.sticky === !!stored.entryData?.sticky],
    ['stickyEmail', (sent.entryData.stickyEmail ?? StickyEmail.No) === (stored.entryData?.stickyEmail ?? StickyEmail.No)],
    ['publishedAt', sent.publishedAt === stored.publishedAt],
    ['status', sent.status === stored.status],
  ]
  return checks.filter(([, equal]) => !equal).map(([field]) => field)
}

export const filterEditorialCards = (cards: EditorialCard[], search: string) => {
  const query = search.trim().toLowerCase()
  if (!query) return cards
  return cards.filter((card) => editorialCardLanguages.some((language) => card.titles?.[language]?.toLowerCase().includes(query)))
}

// The backend stores an 800 px wide copy of every `htmleditor` upload next to the original.
export const toHtmlEditorImageUrl = (url: string) => {
  const dotIndex = url.lastIndexOf('.')
  if (dotIndex <= url.lastIndexOf('/')) return `${url}-800`
  return `${url.substring(0, dotIndex)}-800${url.substring(dotIndex)}`
}

const dateTimeLocalFormat = "yyyy-MM-dd'T'HH:mm"

export const toDateTimeLocalValue = (timestamp: number) => (Number.isFinite(timestamp) ? format(timestamp, dateTimeLocalFormat) : '')

export const fromDateTimeLocalValue = (value: string) => {
  const date = parse(value, dateTimeLocalFormat, new Date())
  return isValid(date) ? date.getTime() : NaN
}

export const isValidActionTarget = (target: string) => /^https?:\/\/\S+$/i.test(target.trim())

const sameForEveryLanguage = (text: string): ContentLanguageTranslation => ({ en: text, ja: text, zh: text })

// What a user with `language` sees: the SaaS falls back to EN for the content and the button label, but not for the titles.
export const toPreviewNewsEntry = (values: EditorialCardFormValues, language: EditorialCardLanguage, id = ''): UnifiedNewsEntry => {
  const withFallback = (map: EditorialCardTranslations) => map[language] || map.en
  const entryData = {
    content: sameForEveryLanguage(withFallback(values.contents)),
    ...(values.image ? { image: values.image } : {}),
    action: {
      type: values.actionType,
      target: values.actionTarget,
      label: sameForEveryLanguage(withFallback(values.actionLabels)),
    },
    sticky: values.sticky,
  } as unknown as NewsEntry['entryData']
  return {
    feedEntry: {
      id,
      type: NewsEntryType.Editorial,
      publishedAt: values.publishedAt,
      titles: sameForEveryLanguage(values.titles[language]),
      subtitles: sameForEveryLanguage(values.subtitles[language]),
      entryData,
      gameId: '',
      market: 'us',
    },
    hasFTUEVideo: 0,
  }
}
